import React, {createContext, useCallback, useContext, useMemo} from "react";
import PropTypes from "prop-types";
import {useGTMDispatch} from "@elgorditosalsero/react-gtm-hook";
import {useCookies} from "react-cookie";
import {merge} from "lodash";
import {useKeycloakFunctions, useSessionStorage} from "../../hooks";
import {UserContext } from "./UserProvider";
import {LOCAL, DEV_ENV, STAGE_ENV, CUSTOMER, CUSTOMER_MEMBER, CUSTOMER_PROSPECT, CUSTOMER_TRIAL, CUSTOMER_EMPLOYEE} from "../../common/constant";
import {LocaleContext} from "./LocaleProvider";
import {CATEGORIES, LocalizedContext} from "./LocalizedProvider";
import {LANGUAGE} from "../../common/i18n/locales/localeConstant";
import {uuidv4} from "../../common/common";

const ANALYTICS_QUEUE_STORAGE_KEY = "analytics-event-queue";

/**
 * The context
 */
export const AnalyticsContext = createContext();

/**
 * The Provider
 */
const AnalyticsProvider = ({children}) => {
    const {locale} = useContext(LocaleContext)[0];
    const [userState] = useContext(UserContext);
    const [localizedState] = useContext(LocalizedContext);

    const sendDataToGTM = useGTMDispatch();
    const {initialized, authenticated, userId, hasOneOfRoles} = useKeycloakFunctions();
    const [cookies] = useCookies(["_ga"]);
    const [eventQueue, setEventQueue] = useSessionStorage(ANALYTICS_QUEUE_STORAGE_KEY);

    const userTypes = {
        MEMBER: "member",
        CUSTOMER: "customer",
        PROSPECT: "prospect",
        TRIAL: "trial"
    };

    const getEnvironment = () => {
        if(location) {
            if (location.host?.includes(LOCAL) || location.host?.includes(DEV_ENV)) {
                return "dev";
            } else if (location.host?.includes(STAGE_ENV)) {
                return "stage";
            } else {
                return "live";
            }
        }
    };

    const getTrafficType = () => {
        // TODO: it would be "dev | internal" but we cannot distinguish, so leave empty for now
        return;
    };

    const getUserType = () => {
        //Order matters here.
        if (hasOneOfRoles([CUSTOMER_MEMBER])) {
            return userTypes.MEMBER;
        } else if (hasOneOfRoles([CUSTOMER_TRIAL])) {
            return userTypes.TRIAL;
        } else if (hasOneOfRoles([CUSTOMER])) {
            return userTypes.CUSTOMER;
        } else if (hasOneOfRoles([CUSTOMER_PROSPECT])) {
            return userTypes.PROSPECT;
        } else {
            return;
        }
    };

    const getUserAgent = () => {
        const userAgent = navigator?.userAgent;
        const regex1 = /Mozilla\/([0-9]{1,}\.[0-9]{1,})/gmi;
        const regex2 = /(\(KHTML, like Gecko\) )/gmi;
        const shortUserAgent = userAgent?.replace(regex1, "")?.replace(regex2, "")?.trim();
        const noValue = "no Value";

        if(userAgent && shortUserAgent?.length <= 100){
            return shortUserAgent;
        }
        if(userAgent && shortUserAgent?.length > 100){
            return "too long string";
        }
        return noValue;
    };

    const getClientId = () => {
        const _ga = cookies["_ga"];
        return _ga?.split(".")?.slice(-2)?.join(".");
    };

    const pushEvent = useCallback((data) => {
        return new Promise((resolve) => {
            if (sendDataToGTM) {
                setTimeout(() => {
                    sendDataToGTM({...data, event_uuid: uuidv4()});
                    resolve();
                }, 600);
            }
        });
    }, [sendDataToGTM]);

    const getEnglishSlugFromLocalizedState = useCallback((localizedSlug, type) => {
        if(localizedState && locale) {
            return localizedState.idsMap?.[localizedState?.slugsMap?.[locale]?.[type]?.[localizedSlug]]?.["slug_" + LANGUAGE.EN.replace("-", "_")];
        }
    }, [localizedState, locale]);

    const getProductImpressionData = useCallback((resortId, resortName, countrySlug, destinationSlug, price, currency, data) => {
        const coreData = {
            item_name: resortName,
            item_id: resortId,
            currency: currency, // "EUR"
            discount: undefined,
            item_brand: "Hapimag",
            item_category: getEnglishSlugFromLocalizedState(countrySlug, CATEGORIES.COUNTRIES),
            item_region: getEnglishSlugFromLocalizedState(destinationSlug, CATEGORIES.DESTINATIONS),
            item_availability: "--", // to be set by 'data' object. values: "Available" |  "Unavailable" or "--" if dates not available,
            item_date_in: undefined, // to be set by 'data' object. format: "YYYY-MM-DD"
            item_date_out: undefined, // to be set by 'data' object. format: "YYYY-MM-DD"
            item_date_flexibility: undefined, // to be set by 'data' object
            item_nights: undefined, // to be set by 'data' object
            item_list_name: undefined, // to be set by 'data' object. values: "Search Results with/without Price"
            item_list_id: undefined, // to be set by 'data' object. values: "search-no/with-price"
            index: undefined, // if there is an index, to be set by 'data' object
            quantity: 1,
            price: price
        };

        return {...coreData, ...data};
    }, [getEnglishSlugFromLocalizedState]);

    const getApartmentItemData = useCallback((resortDetails, apartment, price, currency, data) => {
        const coreData = {
            item_name: getEnglishSlugFromLocalizedState(resortDetails.slug, CATEGORIES.RESORTS),
            item_id: resortDetails.hapimagResortId,
            discount: undefined,
            currency: currency,
            item_brand: "Hapimag",
            item_category: getEnglishSlugFromLocalizedState(resortDetails.destination.country.slug, CATEGORIES.COUNTRIES),
            item_region: getEnglishSlugFromLocalizedState(resortDetails.destination.slug, CATEGORIES.DESTINATIONS),
            item_room_type: apartment.apartment_classification,
            item_room_category: apartment.wheelchair_accessibility ? "Wheelchair" : "Standard",
            item_rooms: apartment.number_of_rooms,
            item_guests: apartment.max_number_of_guests,
            item_availability: "--", // to be set by 'data' object. values: "Available" |  "Unavailable"
            item_date_in: undefined, // to be set by 'data' object. format: "YYYY-MM-DD"
            item_date_out: undefined, // to be set by 'data' object. format: "YYYY-MM-DD"
            item_date_flexibility: undefined, // to be set by 'data' object
            item_nights: undefined, // to be set by 'data' object
            item_list_name: undefined, // to be set by 'data' object. values: "Search Results with/without Price"
            item_list_id: undefined, // to be set by 'data' object. values: "search-no/with-price"
            index: undefined, // if there is an index, to be set by 'data' object
            quantity: 1,
            price: price
        };

        return {...coreData, ...data};
    }, [getEnglishSlugFromLocalizedState]);

    const getResortAndApartmentData = useCallback((resortDetails, apartment, guests) => {
        let coreData = {
            resort_name: getEnglishSlugFromLocalizedState(resortDetails?.slug, CATEGORIES.RESORTS),
            resort_id: resortDetails?.hapimagResortId,
            resort_country: getEnglishSlugFromLocalizedState(resortDetails?.destination.country.slug, CATEGORIES.COUNTRIES),
            resort_region: getEnglishSlugFromLocalizedState(resortDetails?.destination.slug, CATEGORIES.DESTINATIONS)
        };

        if(apartment) {
            const apartmentData = {
                room_type: apartment?.apartment_name,
                room_category: apartment?.wheelchair_accessibility ? "Wheelchair" : "Standard",
                room_details: `${apartment?.number_of_rooms} room / ${guests} guests`
            };
            coreData = {...coreData, ...apartmentData};
        }

        return coreData;
    }, [getEnglishSlugFromLocalizedState]);

    // TODO: this function will be obsolete after the arrival card v1 is not used any more
    const getArrivalCardData = useCallback((resortDetails, reservation, arrivalTime, additionalWishes) => {
        const data = {
            resort_name: getEnglishSlugFromLocalizedState(resortDetails?.slug, CATEGORIES.RESORTS),
            resort_id: resortDetails?.hapimagResortId,
            resort_country: getEnglishSlugFromLocalizedState(resortDetails?.destination.country.slug, CATEGORIES.COUNTRIES),
            resort_region: getEnglishSlugFromLocalizedState(resortDetails?.destination.slug, CATEGORIES.DESTINATIONS),
            guests_no: reservation?.guests,
            arrival_time: arrivalTime || "-",
            arrival_date: reservation?.date_from,
            departure_date: reservation?.date_to,
            additional_wishes: additionalWishes?.length > 0 ? additionalWishes.join("|") : "-"
        };

        return data;
    }, [getEnglishSlugFromLocalizedState]);

    const events = {
        PAGE_VIEW: "virtual_page_view",
        CUSTOM_EVENT: "custom_event",
        EEC: "EEC",
    };

    const eventNames = {
        LOGIN: "login",
        LOGOUT: "logout",
        SIGN_UP_START: "sign_up_start",
        SIGN_UP_START_SIDEMENU: "sign_up_start_sidemenu",
        SIGN_UP_VERIFIED: "sign_up_verified",
        UPGRADE_TO_TRIAL: "upgrade_to_trial",
        NEWSLETTER_PROMPT_VIEW: "newsletter_prompt_view",
        NEWSLETTER_SUBSCRIBE: "newsletter_subscribe",
        NEWSLETTER_CLOSE: "newsletter_close",
        SHARES_SELL_BTN: "shares_sell_btn",
        SHARES_CREATE_OFFER: "shares_create_offer",
        SHARES_START_OFFER: "shares_start_offer",
        VISIT_MARKETPLACE_BTN: "visit_marketplace_btn",
        SHARES_OFFER_OPEN_DETAILS: "shares_offer_open_details",
        SHARES_OFFER_SELL_BTN: "shares_offer_sell_btn",
        SHARES_OFFER_REQUEST_DOCS_NEXT: "shares_offer_request_docs_next",
        SHARES_OFFER_REQUEST_DOCS_FINAL: "shares_offer_request_docs_final",
        SHARES_OFFER_FINAL_CHECK: "shares_offer_final_check",
        CONTACT_SELLER_START: "contact_seller_start",
        CONTACT_SELLER_CONTINUE: "contact_seller_continue",
        CONTACT_SELLER_MSG_SUCCESS: "contact_seller_msg_success",
        ADD_PAYMENT_INFO: "add_payment_info",
        PURCHASE: "purchase",
        VIEW_ITEM: "view_item",
        ADD_TO_CART: "add_to_cart",
        BOOKING_CANCELLATION_START: "booking_cancellation_start",
        BOOKING_CANCELLATION_CONFIRMED: "booking_cancellation_confirmed",
        REFUND: "refund",
        ADD_TO_WISHLIST: "add_to_wishlist",
        REMOVE_FROM_WAITING_LIST: "remove_from_waitinglist",
        DELETE_WAITING_LIST: "delete_waitinglist",
        VIEW_ITEM_LIST: "view_item_list",
        SELECT_ITEM: "select_item",
        BEGIN_CHECKOUT: "begin_checkout",
        POINTS_BUY_BTN: "points_buy_btn",
        POINTS_FIND_OFFER: "points_find_offer",
        POINTS_BUY_NOW: "points_buy_now",
        POINTS_SELL_BTN: "points_sell_btn",
        POINTS_SELL_SELECT: "points_sell_select",
        POINTS_IBAN_SET: "points_iban_set",
        POINTS_PUBLISHED_OFFER: "points_published_offer",
        POINTS_PUBLISHED_ERROR: "points_published_error",
        POINTS_BACK_TO_OVERVIEW: "points_back_to_overview",
        POINTS_SELL_MORE_BTN: "points_sell_more_btn",
        WAITING_LIST_OFFER_ACCEPTED: "waiting_list_offer_accepted",
        WAITING_LIST_OFFER_DECLINED: "waiting_list_offer_declined",
        OVERLAPPING_BOOKING_REPLACED: "overlapping_booking_replaced",
        OVERLAPPING_BOOKING_ACTIVE: "overlapping_booking_active",
        WAITING_LIST_EDIT_BUTTON: "waiting_list_edit_btn",
        WAITING_LIST_DELETE_BUTTON: "waiting_list_delete_btn",
        WAITING_LIST_REMOVE_ENTRY: "waiting_list_remove_entry",
        WAITING_LIST_REMOVAL_CONFIRMED: "waiting_list_removal_confirmed",
        WAITING_LIST_DELETION_CONFIRMED: "waiting_list_deletion_confirmed",
        WAITING_LIST_ADD_BUTTON: "waiting_list_add_btn",
        NAVIGATION: "navigation",
        SIDEMENU_FOLD: "sidemenu_fold",
        SIDEMENU_UNFOLD: "sidemenu_unfold",
        CHANGE_LOCALE: "change_locale",
        CHANGE_CURRENCY: "change_currency",
        ARRIVAL_CARD_EDIT: "arrival_card_edit",
        ARRIVAL_CARD_SAVED: "arrival_card_saved",
        EXISTING_GUEST_ADDED: "existing_guest_added",
        EXISTING_GUEST_REMOVED: "existing_guest_removed",
        NEW_GUEST_CREATED: "new_guest_created",
        SELECT_MAINGUEST_MYSELF: "select_mainguest_myself",
        SELECT_MAINGUEST_SOMEBODYELSE: "select_mainguest_somebodyelse",
        EXISTING_MAIN_GUEST_ADDED: "existing_mainguest_added",
        ARRIVAL_TIME_DEFINED: "arrival_time_defined",
        ARRIVAL_TIME_REMOVED: "arrival_time_removed",
        ARRIVAL_TIME_UPDATED: "arrival_time_updated",
        ADD_WISH: "add_wish",
        REMOVE_WISH: "remove_wish",
        CONTACT_HOST: "contact_host",
        COPY_HOST_EMAIL: "copy_host_email",
        COPY_HOST_PHONE: "copy_host_phone",
        SERVICE_SLIDER: "service_slider",
        SERVICE_SELECT: "service_selection",
        SERVICE_REQUEST_SELECT: "service_request_select",
        SERVICE_REQUEST_SEND: "service_request_send",
        SERVICE_REQUEST_ERROR: "service_request_error",
        SERVICE_REQUEST_OPEN: "service_request_open",
        SERVICE_RESERVATION_SELECT: "service_reservation_select",
        SERVICE_RESERVATION_CONFIRMED: "service_reservation_confirmed",
        SERVICE_RESERVATION_ERROR: "service_reservation_error",
        SERVICE_RESERVATION_CANCELED: "service_reservation_canceled",
        COMPLETE_PRECHECKIN: "complete_pre_checkin",
        REQUEST_CALL_START: "request_call_start",
        REQUEST_CALL_SEND_BTN: "request_call_send_btn",
        REQUEST_CALL_ERROR: "request_call_error",
        REQUEST_CALL_SUCCESS: "request_call_success",
        REQUEST_CALL_ERROR_RESPONSE: "request_call_error_response",
        PRINT_BUTTON_CLICK: "print_button_click",
        REQUEST_PRINT_ALL_BUTTON_CLICK: "request_print_all_button_click",
        PRINT_HOLIDAY_DETAILS: "print_holiday_details",
        TRIALPROMPT_BANNER_IMPRESSION: "trialprompt_banner_impression",
        TRIALPROMPT_BANNER_CONTACTUS: "trialprompt_banner_contactus",
        TRIALPROMPT_BANNER_BEMEMBER: "trialprompt_banner_bemember",
        TRIALPROMPT_BANNER_CLOSE: "trialprompt_banner_close",
        UNLOCKTRIP_LIGHTBOX_IMPRESSION: "unlocktrip_lightbox_impression",
        UNLOCKTRIP_LIGHTBOX_BEMEMBER: "unlocktrip_lightbox_bemember",
        UNLOCKTRIP_LIGHTBOX_MOREABOUT: "unlocktrip_lightbox_moreabout",
        THREE_BOOKING_POPUP_IMPRESSION: "3booking_popup_impression",
        THREE_BOOKING_POPUP_MOREABOUT: "3booking_popup_moreabout",
        THREE_BOOKING_POPUP_BEMEMBER: "3booking_popup_bemember",
        THREE_BOOKING_POPUP_CLOSE: "3booking_popup_close",
        BUYSHARE_POPUP_IMPRESSION: "buyshare_popup_impression",
        BUYSHARE_POPUP_TOMARKETPLACE: "buyshare_popup_tomarketplace",
        BUYSHARE_POPUP_TOHAPIMAG: "buyshare_popup_tohapimag",
        BUYSHARE_POPUP_MOREABOUT_MRKT: "buyshare_popup_moreabout_mrkt",
        BUYSHARE_POPUP_MOREABOUT_HPMG: "buyshare_popup_moreabout_hpmg",
        BUYSHARE_POPUP_CLOSE: "buyshare_popup_close",
        SHARES_REQUEST_OPEN_DETAILS: "shares_request_open_details",
        SHARES_REQUEST_WITHDRAW: "shares_request_withdraw",
        SHARES_REQUEST_WITHDRAW_CANCEL: "shares_request_withdraw_cancel",
        SHARES_REQUEST_WITHDRAW_CONFIRM: "shares_request_withdraw_confirm",
        SHARES_REQUEST_REMOVE: "shares_request_remove",
        SHARES_REQUEST_REMOVE_CANCEL: "shares_request_remove_cancel",
        SHARES_REQUEST_REMOVE_CONFIRM: "shares_request_remove_confirm",
        SHARES_OFFER_REQUEST_DOCS_BTN: "shares_offer_request_docs_btn",
        SHARES_OFFER_DECLINE: "shares_offer_decline",
        SHARES_OFFER_DECLINE_CANCEL: "shares_offer_decline_cancel",
        SHARES_OFFER_DECLINE_CONFIRM: "shares_offer_decline_confirm",
        SHARES_OFFER_EMAIL_SELECT: "shares_offer_email_select",
        SHARES_OFFER_EMAIL_COPY: "shares_offer_email_copy",
        SHARES_OFFER_PHONE_SELECT: "shares_offer_phone_select",
        SHARES_OFFER_DELETE: "shares_offer_delete",
        SHARES_OFFER_DELETE_CANCEL: "shares_offer_delete_cancel",
        SHARES_OFFER_DELETE_CONFIRM: "shares_offer_delete_confirm",
        SHARES_OFFER_REMOVE: "shares_offer_remove",
        SHARES_OFFER_REMOVE_CANCEL: "shares_offer_remove_cancel",
        SHARES_OFFER_REMOVE_CONFIRM: "shares_offer_remove_confirm",
        MANAGE_GUESTS_POPUP_CLICK: "manageguests_popup_click",
        MANAGE_GUESTS_POPUP_IMPRESSION: "manageguests_popup_impression",
        MANAGE_GUESTS_POPUP_CONFIRM: "manageguests_popup_confirm",
        MANAGE_GUESTS_POPUP_CANCEL: "manageguests_popup_cancel",
        DIRECT_SALES_ADVICE_BTN: "direct_sales_advice_btn",
        DIRECT_SALES_BUYNOW_BTN: "direct_sales_buynow_btn",
        DIRECT_SALES_OPENCONTRACT_POPUP: "direct_sales_opencontract_popup",
        DIRECT_SALES_ADVICE_POPUP: "direct_sales_advice_popup",
        DIRECT_SALES_CLOSE_POPUP: "direct_sales_close_popup",
        DIRECT_SALES_PROMPT_VIEW: "direct_sales_prompt_view",
        DIRECT_SALES_ACCOUNT_POPUP: "direct_sales_account_popup",
        DIRECT_SALES_START_REGISTRATION_BTN: "direct_sales_start_registration_btn",
        DIRECT_SALES_SIGN_IN_BTN: "direct_sales_sign_in_btn",
        DIRECT_SALES_NOSHARES_POPUP: "direct_sales_noshares_popup",
        DIRECT_SALES_CONTINUE_BTN: "direct_sales_continue_btn",
        DIRECT_SALES_CANCEL_BTN: "direct_sales_cancel_btn",
        DIRECT_SALES_EDIT_DETAILS_BTN: "direct_sales_edit_details_btn",
        DIRECT_SALES_EDIT_ORDER_BTN: "direct_sales_edit_order_btn",
        DIRECT_SALES_CONFIRM_BTN: "direct_sales_confirm_btn",
        DIRECT_SALES_CONTRACT_DOWNLOAD: "direct_sales_contract_download",
        DIRECT_SALES_CONTRACT_UPLOAD: "direct_sales_contract_upload",
        ALTERNATIVE_DATES_BTN: "alternative_dates_btn",
        WAITING_LIST_JOIN_BTN: "waiting_list_join_btn",
        ADD_FILTER_BUTTON: "add_filter_btn",
        CHANGE_GUESTS_NUMBER: "change_guests_no",
        VIEW_PROMOTION: "view_promotion",
        SELECT_PROMOTION: "select_promotion",
        PARTNER_BOOK_NOW: "partner_book_now",
        PARTNER_FIND_STAY: "partner_find_stay",
        PARTNER_COPY_CODE: "partner_copy_code",
        OPEN_AVAILABILITY_CALENDAR: "open_availability_calendar",
        UPDATE_ROOM_CATEGORY: "update_room_category",
        UPDATE_DATES: "update_dates",
        JOIN_WAITINGLIST_FAILED: "join_waitinglist_failed",
        LINKTO_REFERRAL_PAGE: "linkto_referral_page",
        UPDATE_GUESTS_NO: "update_guests_no",
        VISIT_RESORT_PAGE_BTN: "visit_resort_page_btn",
        SIMILAR_BOOKING_1_CHECK_IMPRESSION: "similar_booking_1stcheck_impression",
        SIMILAR_BOOKING_1_CHECK_CONFIRMATION: "similar_booking_1stcheck_confirmation",
        SIMILAR_BOOKING_1_CHECK_VIEWTRIPS: "similar_booking_1stcheck_viewtrips",
        SIMILAR_BOOKING_1_CHECK_ABORT: "similar_booking_1stcheck_abort",
        SIMILAR_BOOKING_2_CHECK_IMPRESSION: "similar_booking_2ndcheck_impression",
        SIMILAR_BOOKING_2_CHECK_REVIEW: "similar_booking_2ndcheck_review",
        SIMILAR_BOOKING_2_CHECK_VIEWTRIPS: "similar_booking_2ndcheck_viewtrips",
    };

    const loginTypes = {
        AUTO: "auto",
        MANUAL: "manual"
    };

    const sendPageView = useCallback((data) => {
        if(pushEvent && initialized && location && location.pathname) {
            if(authenticated && !userState?.userSettings?.member_id) {
                return;
            }
            const coreData = {
                event: events.PAGE_VIEW,
                environment: getEnvironment(),
                traffic_type: getTrafficType(),
                page_path: `${location?.pathname}${location?.search}`,
                page_type: "",
                page_title: document.title,
                page_locale: locale,
                login_status: authenticated,
                user_id: userId,
                user_type: getUserType(),
                employee: hasOneOfRoles([CUSTOMER_EMPLOYEE]),
                newsletter_subscriber: userState?.userSettings?.receive_newsletter || false,
                srp: null,
                user_agent: getUserAgent(),
                client_id: getClientId()
            };
            const payload = {...coreData, ...data};
            pushEvent(payload);
        }
    }, [pushEvent, location?.pathname, location?.search, initialized, authenticated, userId, userState?.userSettings?.member_id]);

    const sendCustomEvent = useCallback((data) => {
        if(pushEvent) {
            const coreData = {
                event: events.CUSTOM_EVENT,
                event_name: null,
            };
            const payload = {...coreData, ...data};
            pushEvent(payload);
        }
    }, [pushEvent]);

    const sendLogin = useCallback((data) => {
        if(pushEvent && initialized) {
            if(authenticated && !userState?.userSettings?.member_id) {
                return;
            }
            const coreData = {
                event: events.CUSTOM_EVENT,
                event_name: eventNames.LOGIN,
                login_status: "true",
                user_id: userId,
                user_type: getUserType(),
                employee: hasOneOfRoles([CUSTOMER_EMPLOYEE]),
                newsletter_subscriber: userState?.userSettings?.receive_newsletter || false,
                login: {
                    method: "email",
                    login_type: loginTypes.AUTO
                }
            };
            const payload = merge(coreData, data);
            pushEvent(payload);
        }
    }, [pushEvent, initialized, authenticated, userId, userState?.userSettings?.member_id]);

    const sendEcommerceEvent = useCallback((eventName, data) => {
        if (sendDataToGTM && pushEvent && initialized) {
            sendDataToGTM({ecommerce: null});

            const coreData = {
                event: `${events.EEC}_${eventName}`,
                event_name: eventName,
                user_type: getUserType()
            };

            const payload = {
                ...coreData,
                ecommerce: {
                    ...data
                }
            };

            return pushEvent(payload);
        }

        return Promise.reject(new Error("Missing dependencies: sendDataToGTM, pushEvent, or initialized."));
    }, [sendDataToGTM, pushEvent, initialized]);

    const sendArrivalCardEvent = useCallback((eventName, extraData, resortDetails) => {
        if(pushEvent) {

            const coreEventData = {
                resort_name: getEnglishSlugFromLocalizedState(resortDetails?.slug, CATEGORIES.RESORTS),
                resort_id: resortDetails?.hapimagResortId,
                resort_country: getEnglishSlugFromLocalizedState(resortDetails?.destination.country.slug, CATEGORIES.COUNTRIES),
                resort_region: getEnglishSlugFromLocalizedState(resortDetails?.destination.slug, CATEGORIES.DESTINATIONS),
            };

            const payload = {
                event: events.CUSTOM_EVENT,
                event_name: eventName,
                [eventName]: extraData ? {...coreEventData, ...extraData} : coreEventData
            };
            pushEvent(payload);
        }
    }, [pushEvent]);

    const addEventToQueue = useCallback((event) => {
        const queue = eventQueue || [];
        queue?.push({
            ...event,
            id: new Date().valueOf()
        });

        setEventQueue(ANALYTICS_QUEUE_STORAGE_KEY, queue);
    }, [eventQueue]);

    const removeEventFromQueue = useCallback((id) => {
        const tmpArr = eventQueue?.filter((ev) => ev?.id !== id);
        setEventQueue(ANALYTICS_QUEUE_STORAGE_KEY, tmpArr);
    }, [eventQueue]);

    const memoedValues = useMemo(() => ({
        sendPageView,
        sendCustomEvent,
        sendLogin,
        sendEcommerceEvent,
        sendArrivalCardEvent,
        getEnglishSlugFromLocalizedState,
        getProductImpressionData,
        getApartmentItemData,
        getResortAndApartmentData,
        getArrivalCardData,
        eventQueue,
        addEventToQueue,
        removeEventFromQueue,
        eventNames,
        userTypes,
        loginTypes,
        pageTypes: {
            HOME: "home",
            RESORTS: "search-results",
            RESORT_DETAILS: "resort-details",
            BOOKING_INFORMATION: "booking-information",
            RESERVATION_DETAILS: "reservation-details",
            AVAILABILITY_OVERVIEW: "availability-overview",
        },
        pageTitles: {
            BOOKING_INFORMATION: "Hapimag | Booking Information",
            RESERVATION_DETAILS: ["Hapimag - Trip Details - Reservation", "Hapimag - Trip Details - Pre Check-In", "Hapimag - Trip Details - Services"],
            AVAILABILITY_OVERVIEW: "Availability Overview",
        },
        itemListNames: {
            SEARCH_RESULTS_WITH_PRICE: "Search Results with Price",
            SEARCH_RESULTS_WITHOUT_PRICE: "Search Results without Price",
            SUGGESTIONS_WITH_PRICE: "Suggestions with Price",
            SUGGESTIONS_WITHOUT_PRICE: "Suggestions without Price",
            AVAILABILITY_CALENDAR_LIST: "Availability Calendar List"
        },
        itemListIds: {
            SEARCH_WITH_PRICE: "search-with-price",
            SEARCH_NO_PRICE: "search-no-price",
            SUGGESTIONS_WITH_PRICE: "suggestions-with-price",
            SUGGESTIONS_NO_PRICE: "suggestions-no-price",
            AVAILABILITY_CALENDAR: "availability-calendar"
        }
    }), [
        sendPageView,
        sendCustomEvent,
        sendLogin,
        sendEcommerceEvent,
        sendArrivalCardEvent,
        getEnglishSlugFromLocalizedState,
        getProductImpressionData,
        getApartmentItemData,
        getResortAndApartmentData,
        getArrivalCardData,
        eventQueue,
        addEventToQueue,
        removeEventFromQueue
    ]);

    return (
        <AnalyticsContext.Provider value={memoedValues}>
            {children}
        </AnalyticsContext.Provider>
    );
};

AnalyticsProvider.propTypes = {
    children: PropTypes.any
};

function useAnalytics() {
    const context = useContext(AnalyticsContext);
    if (context === undefined) {
        throw new Error("useAnalytics must be used within a AnalyticsProvider");
    }
    return context;
}

export {AnalyticsProvider, useAnalytics};
